import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageSquare, Star, Check, Building2, MapPin } from 'lucide-react';
import { getProfile } from '../../services/profileService';
import { CallMethodModal } from '../CallMethodModal/CallMethodModal';
import { TenureChip } from '../TenureBadge/TenureBadge';
import { deptScheme } from '../../utils/deptColors';
import { teamsChatUrl, teamsCallUrl, telUrl } from '../../utils/deepLinks';

/**
 * Grid-view person card.
 *
 * Rebuilt to the mockup's `.pcard`: a dense card with an avatar-and-name header,
 * key-value rows and a footer, rather than a 4:3 photo banner with the body
 * beneath it. The banner made each card ~300px tall, so a 739-person directory
 * showed a dozen people per screen and the grid was a scroll marathon.
 *
 * NOTE: this trades a large photo for a 56px avatar. Photos still show — faces
 * are the fastest way to recognise a colleague — but they are no longer the
 * dominant element. If the big photos are wanted back, this is the decision to
 * revisit.
 *
 * The per-department colour hash is reduced to a 6px identity dot (spec decision
 * 8). It previously tinted the border, the ring, the shadow, the photo backdrop
 * and the department pill, which turned a grid into a colour chart.
 */

function availabilityColor(status) {
  if (!status) return 'bg-slate-400';
  const s = status.toLowerCase();
  if (s === 'available' || s === 'availableidle') return 'bg-emerald-500';
  if (s === 'busy' || s === 'busyidle') return 'bg-red-500';
  if (s === 'away') return 'bg-yellow-500';
  if (s === 'donotdisturb' || s === 'dnd') return 'bg-red-600';
  if (s === 'berightback' || s === 'brb') return 'bg-yellow-300';
  return 'bg-slate-400';
}

function availabilityLabel(status) {
  if (!status) return 'Unknown';
  const s = status.toLowerCase();
  if (s === 'available') return 'Available';
  if (s === 'availableidle') return 'Available (Idle)';
  if (s === 'busy') return 'Busy';
  if (s === 'busyidle') return 'Busy (Idle)';
  if (s === 'away') return 'Away';
  if (s === 'donotdisturb' || s === 'dnd') return 'Do Not Disturb';
  if (s === 'berightback' || s === 'brb') return 'Be Right Back';
  if (s === 'offline') return 'Offline';
  return 'Unknown';
}

function KeyValue({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <div className="flex items-center gap-[7px] text-[11.5px] text-ink-2 min-w-0">
      <Icon size={12} className="flex-none text-ink-4" />
      <b className="font-semibold truncate">{children}</b>
    </div>
  );
}

export function EmployeeCard({
  employee,
  photo,
  onClick,
  index = 0,
  isSelected = false,
  onToggleSelection,
  isFavorite = false,
  onToggleFavorite,
  hasAnySelected = false,
  currentUserId: _currentUserId = null,
  onBadgesClick,
}) {
  const [showCallModal, setShowCallModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  const customProfile = getProfile(employee.id);
  const displayPhoto = customProfile?.photo || employee.openAssetPhoto || photo;

  // Top skills: self-listed first, then firm-curated (OpenAsset)
  const cardSkills = [
    ...(customProfile?.skills ?? []),
    ...(customProfile?.areasOfExpertise ?? []),
    ...(employee.skills ?? []),
  ].filter((skill, i, arr) => arr.indexOf(skill) === i);

  const initials = employee.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const scheme = deptScheme(employee.department);
  const showCheckbox = isHovered || hasAnySelected;
  const hasCallOptions = employee.mobilePhone || employee.businessPhones?.[0] || employee.mail;

  const handleCallClick = (e) => {
    e.stopPropagation();
    const methods = [!!employee.mobilePhone, !!(employee.businessPhones?.[0]), !!employee.mail].filter(Boolean);
    if (methods.length > 1) {
      setShowCallModal(true);
    } else {
      // Every builder returns null for an unusable value, so each branch has to
      // check before navigating — assigning null to location.href would send the
      // browser to a page called "null".
      const tel = telUrl(employee.mobilePhone) || telUrl(employee.businessPhones?.[0]);
      if (tel) {
        window.location.href = tel;
        return;
      }
      const call = teamsCallUrl(employee.mail);
      if (call) window.open(call, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, delay: Math.min(index, 12) * 0.02, ease: [0.22, 1, 0.36, 1] }}
      className="group relative h-full"
    >
      <div
        ref={cardRef}
        onClick={() => onClick?.(employee, cardRef.current?.getBoundingClientRect())}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        /* No blanket padding or row gap here on purpose: the footer is a
           full-bleed recessed tray, so it has to reach the card's edges.
           Padding lives on the three zones below instead. Vertical rhythm is
           14 / 11 / 11 / 11+11 / 14: the facts zone carries its own bottom
           11px of card-plane space so the tray seam never touches the office
           text (the card is 11px taller than the pre-tray design for it). */
        className={`relative flex flex-col h-full rounded-card cursor-pointer
                    bg-surface-card border transition-[box-shadow,border-color,transform] duration-150
                    hover:-translate-y-[2px]
                    ${isSelected
                      ? 'border-accent shadow-e2 bg-accent-wash'
                      : 'border-line shadow-e1 hover:border-accent-line hover:shadow-e2'}`}
      >
        {/* Header: avatar + name + title */}
        <div className="flex items-start gap-[11px] min-w-0 p-3.5 pb-[11px]">
          <div className="relative flex-none">
            {displayPhoto ? (
              <img
                src={displayPhoto}
                alt=""
                className="w-14 h-14 rounded-full object-cover object-top ring-1 ring-line-strong"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-surface-2 ring-1 ring-line-strong flex items-center justify-center">
                <span className="text-[15px] font-semibold text-ink-3 select-none">{initials}</span>
              </div>
            )}
            <span
              className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-surface-card ${availabilityColor(employee.availability)}`}
              title={availabilityLabel(employee.availability)}
            />

            {/* Selection checkbox */}
            <motion.button
              onClick={(e) => { e.stopPropagation(); onToggleSelection?.(employee); }}
              initial={false}
              animate={{ opacity: showCheckbox ? 1 : 0, scale: showCheckbox ? 1 : 0.8 }}
              transition={{ duration: 0.13 }}
              aria-label={isSelected ? 'Deselect' : 'Select'}
              className={`absolute -top-1 -left-1 z-10 w-5 h-5 rounded-full border flex items-center justify-center
                shadow-e1 transition-colors
                ${isSelected ? 'bg-accent border-accent text-[var(--on-accent)]' : 'bg-surface-card border-line-strong text-transparent'}
                ${!showCheckbox ? 'pointer-events-none' : ''}`}
            >
              <Check size={11} strokeWidth={3} />
            </motion.button>
          </div>

          <div className="min-w-0 flex-1 pr-5">
            <p className="text-[14px] font-bold tracking-[-0.01em] leading-[1.25] text-ink truncate">
              {employee.displayName}
            </p>
            <p
              className="text-[11.5px] text-ink-3 leading-[1.4] mt-[3px] line-clamp-2"
              title={employee.jobTitle || ''}
            >
              {employee.jobTitle || 'Employee'}
            </p>
          </div>
        </div>

        {/* Favourite — corner, out of the text flow; hidden once the card is
            checkbox-selected so the corner reads as selection, not favoriting. */}
        {!isSelected && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(employee.id); }}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            className={`absolute top-2 right-2 p-1 rounded-full transition-colors
              ${isFavorite ? 'text-amber-400' : 'text-ink-4 opacity-0 group-hover:opacity-100 hover:text-amber-400'}`}
          >
            <Star size={14} fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
        )}

        {/* Inset, not full-bleed — the card's only divider stayed inside the old
            14px padding and moving it to the edges would change light mode. */}
        <div className="h-px bg-line mx-3.5" />

        {/* Facts. Symmetric 11px padding: the bottom 11px is card-plane space
            between the office row and the tray seam — without it the seam sits
            exactly on the text descenders and reads as clipping. */}
        <div className="flex flex-col gap-[7px] min-w-0 px-3.5 pt-[11px] pb-[11px]">
          {employee.department && (
            <div className="flex items-center gap-[7px] text-[11.5px] text-ink-2 min-w-0">
              <span
                className="flex-none w-1.5 h-1.5 rounded-full"
                style={{ background: scheme.accent }}
                aria-hidden="true"
              />
              <b className="font-semibold truncate">{employee.department}</b>
            </div>
          )}
          <KeyValue icon={MapPin}>{employee.officeLocation}</KeyValue>
          {cardSkills.length > 0 && (
            <KeyValue icon={Building2}>
              {cardSkills.slice(0, 2).join(' · ')}
              {cardSkills.length > 2 ? ` +${cardSkills.length - 2}` : ''}
            </KeyValue>
          )}
        </div>

        {/* Footer: tenure + contact actions.
            In dark mode this is a recessed tray (bg-surface-tray + shadow-tray),
            which is what separates "who this is" from "what I can do about it".
            Both tokens are fully transparent in light mode. The inner radius is
            the card radius minus its 1px border, so the tray meets the corner
            cleanly without needing overflow-hidden on the card — which would
            clip the selection checkbox's negative offset. */}
        <div className="flex items-center justify-between gap-2 mt-auto
                        px-3.5 pt-[11px] pb-3.5 rounded-b-[calc(var(--r-card)_-_1px)]
                        bg-surface-tray shadow-tray">
          <TenureChip
            hireDate={employee.hireDate}
            onClick={onBadgesClick ? () => onBadgesClick(employee, cardRef.current?.getBoundingClientRect()) : undefined}
          />

          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {hasCallOptions && (
              <button
                onClick={handleCallClick}
                title={`Call ${employee.displayName}`}
                aria-label={`Call ${employee.displayName}`}
                className="w-7 h-7 rounded-md inline-flex items-center justify-center text-accent
                           hover:bg-accent-soft transition-colors"
              >
                <Phone size={13} />
              </button>
            )}
            {employee.mail && (
              <a
                href={teamsChatUrl(employee.mail)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={`Message ${employee.displayName} in Teams`}
                aria-label={`Message ${employee.displayName} in Teams`}
                className="w-7 h-7 rounded-md inline-flex items-center justify-center text-[#6264A7] dark:text-[#8B8CC7]
                           hover:bg-[#6264A7]/10 dark:hover:bg-[#8B8CC7]/15 transition-colors"
              >
                <MessageSquare size={13} />
              </a>
            )}
          </div>
        </div>
      </div>

      {showCallModal && (
        <CallMethodModal employee={employee} onClose={() => setShowCallModal(false)} />
      )}
    </motion.div>
  );
}
